import React, { useEffect, useState } from 'react';
import ResponsiveNavbar from '../components/shared/ResponsiveNavbar';
import Footer from '../components/shared/Footer';
import { partnersAPI } from '../services/api';
import './Partners.css';

const DEFAULT_TITLE = 'Our Partners';
const DEFAULT_INTRO = 'Veloura collaborates with trusted partners across Los Santos. Explore the services and communities we work with.';

const getPartnerInitials = (name) => (
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
);

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scrolled, setScrolled] = useState(false);
  const [pageTitle, setPageTitle] = useState(DEFAULT_TITLE);
  const [pageIntro, setPageIntro] = useState(DEFAULT_INTRO);
  const [pageHeroUrl, setPageHeroUrl] = useState('');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        const [partnersResponse, settingsResponse] = await Promise.allSettled([
          partnersAPI.getPublic(),
          partnersAPI.getPageSettings()
        ]);

        if (partnersResponse.status === 'fulfilled' && partnersResponse.value.success) {
          setPartners(partnersResponse.value.data || []);
        } else {
          throw partnersResponse.reason || new Error('Failed to load partners.');
        }

        if (settingsResponse.status === 'fulfilled' && settingsResponse.value.success) {
          const settings = settingsResponse.value.data || {};
          setPageTitle(settings.partners_page_title || DEFAULT_TITLE);
          setPageIntro(settings.partners_page_intro || DEFAULT_INTRO);
          setPageHeroUrl(settings.partners_page_hero_url || '');
        }
      } catch (loadError) {
        console.error('Error loading partners:', loadError);
        setError(loadError.message || 'Failed to load partners.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  return (
    <div className="partners-page-wrapper">
      <ResponsiveNavbar logo="VELOURA" logoLink="/" scrolled={scrolled} />

      <main className="partners-page">
        <header
          className={`partners-hero ${pageHeroUrl ? 'has-background' : ''}`}
          style={pageHeroUrl ? { backgroundImage: `url(${pageHeroUrl})` } : undefined}
        >
          <div className="partners-hero-overlay" aria-hidden="true" />
          <div className="partners-hero-inner">
            <span className="partners-eyebrow">The Veloura Network</span>
            <h1>{pageTitle}</h1>
            <p>{pageIntro}</p>
          </div>
        </header>

        <section className="partners-directory" aria-labelledby="partners-directory-title">
          <div className="partners-directory-heading">
            <span className="partners-directory-rule" aria-hidden="true" />
            <div>
              <span className="partners-directory-kicker">Trusted Collaborations</span>
              <h2 id="partners-directory-title">Partner Directory</h2>
            </div>
            {!loading && !error && (
              <span className="partners-count">
                {partners.length} {partners.length === 1 ? 'Partner' : 'Partners'}
              </span>
            )}
          </div>

          {loading && (
            <div className="partners-state" aria-live="polite">
              <span />
              <p>Loading partner organizations...</p>
            </div>
          )}

          {!loading && error && (
            <div className="partners-state error" role="alert">
              <span />
              <p>{error}</p>
            </div>
          )}

          {!loading && !error && partners.length === 0 && (
            <div className="partners-state">
              <span />
              <p>No partners are currently listed.</p>
            </div>
          )}

          {!loading && !error && partners.length > 0 && (
            <div className="partners-list">
              {partners.map((partner) => (
                <article
                  className={`partner-row ${partner.featured ? 'featured' : ''}`}
                  key={partner.id}
                >
                  {partner.background_image_url && (
                    <div
                      className="partner-row-backdrop"
                      style={{ backgroundImage: `url(${partner.background_image_url})` }}
                      aria-hidden="true"
                    />
                  )}

                  <div className="partner-logo-frame">
                    {partner.logo_url ? (
                      <img src={partner.logo_url} alt={`${partner.name} logo`} />
                    ) : (
                      <span aria-hidden="true">{getPartnerInitials(partner.name)}</span>
                    )}
                  </div>

                  <div className="partner-details">
                    <div className="partner-name-line">
                      <h3>{partner.name}</h3>
                      {partner.featured && (
                        <span className="partner-featured-badge">Featured Partner</span>
                      )}
                    </div>
                    {partner.description && (
                      <p className="partner-description">{partner.description}</p>
                    )}
                    <div className="partner-hotline">
                      <span>Business Hotline</span>
                      {partner.business_hotline || 'Not listed'}
                    </div>
                  </div>

                  <div className="partner-action">
                    <a
                      href={partner.site_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="partner-visit-button"
                    >
                      Visit Partner
                    </a>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Partners;
